import React from 'react';
import { connect, ConnectedProps } from '@cerebral/react';
import { state, sequences } from 'app/pwa/app.cerebral';
import { ICON } from 'resources/constants';

import { Wrapper, MenuSection, MenuItem } from './elements';

import { useText, useLink } from 'common/hooks';

type Props = {
  readonly selectedTab: string;
};

const deps = {
  userProviderData: state.appContainer.user.providerData,
  logoutClicked: sequences`appContainer.logoutClicked`,
};

export function Menu({
  userProviderData,
  selectedTab,
  logoutClicked,
}: Props & typeof deps & ConnectedProps): React.ReactElement {
  const t = useText();
  const link = useLink();
  const userHasPassword =
    userProviderData?.some(provider => provider.providerId === 'password') ??
    false;

  const menuList = {
    top: {
      profile: {
        icon: ICON.accountCircle,
        text: t('account.settings.menu.your_profile'),
        link: link('profile'),
      },
      password: {
        icon: ICON.vpnKey,
        text: t('account.settings.menu.reset_password'),
        link: link('profile/password'),
        hide: !userHasPassword,
      },
      language: {
        icon: ICON.language,
        text: t('account.settings.menu.language'),
        link: link('profile/language'),
      },
      emails: {
        icon: ICON.mailOutline,
        text: t('account.settings.menu.email_preferences'),
        link: link('profile/preferences'),
      },
    },
    middle: {
      support: {
        icon: ICON.helpOutline,
        text: t('account.settings.menu.ducky_support'),
        link: link('profile/support'),
      },
      terms: {
        icon: ICON.verifiedUser,
        text: t('account.settings.menu.privacy_terms'),
        link: link('profile/terms'),
      },
    },
    bottom: {
      logout: {
        icon: ICON.powerSettingsNew,
        text: t('account.settings.menu.log_out'),
        onClick: () => logoutClicked(),
      },
    },
  };

  return (
    <Wrapper>
      {Object.entries(menuList).map(([key, menuItems]) => (
        <MenuSection key={`menu-section-${key}`}>
          {Object.entries(menuItems)
            .filter(([, values]) => !values.hide)
            .map(([tab, values]) => (
              <MenuItem
                key={`menu-item-${tab}`}
                icon={values.icon}
                color={'#263238'}
                caption={values.text}
                textColor={'#263238'}
                onClick={values?.onClick}
                href={values?.link}
                selected={selectedTab === tab}
              />
            ))}
        </MenuSection>
      ))}
    </Wrapper>
  );
}

export default connect<Props, typeof deps>(deps, Menu);
